import { useEffect, useState, useCallback } from "react";
import { aiQaList, aiQaCreate, aiQaUpdate, aiQaDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2, Plus, Pencil, Trash2, X, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const empty = { question: "", answer: "", keywords: "" };

export default function MarketingAITab({ userRecordId }) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // row id or "new"
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!userRecordId) return;
    setLoading(true);
    aiQaList(userRecordId)
      .then((res) => {
        setIsAdmin(!!res.is_admin);
        setItems(res.items || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load knowledge base"))
      .finally(() => setLoading(false));
  }, [userRecordId]);

  useEffect(() => { load(); }, [load]);

  function startNew() {
    setDraft(empty);
    setEditing("new");
  }
  function startEdit(it) {
    setDraft({ question: it.question, answer: it.answer, keywords: it.keywords || "" });
    setEditing(it.id);
  }
  function cancel() {
    setEditing(null);
    setDraft(empty);
  }

  async function save() {
    if (!draft.question.trim() || !draft.answer.trim()) {
      return toast.error("Enter both a question and an answer");
    }
    setSaving(true);
    try {
      const payload = {
        user_record_id: userRecordId,
        question: draft.question,
        answer: draft.answer,
        keywords: draft.keywords,
      };
      if (editing === "new") {
        await aiQaCreate(payload);
        toast.success("Added to your AI knowledge base");
      } else {
        await aiQaUpdate(editing, payload);
        toast.success("Updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function remove(it) {
    if (!confirm("Delete this Q&A from your AI knowledge base?")) return;
    try {
      await aiQaDelete(it.id, userRecordId);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  }

  return (
    <div data-testid="marketing-ai-tab">
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white p-5 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={20} />
          <h3 className="text-lg font-semibold">Q&amp;A</h3>
        </div>
        <p className="text-sm text-white/90">
          Teach Molly, your AI assistant, how to answer customer questions.{" "}
          {isAdmin
            ? "These answers power the global OreVital chatbot brain."
            : "These answers are used when customers chat on your dealer page."}
        </p>
      </div>

      {editing && (
        <Card className="rounded-2xl border-violet-200 p-4 mb-4" data-testid="marketing-ai-form">
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">Question / topic</div>
              <Input
                value={draft.question}
                onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                placeholder="e.g. Do your products contain animal ingredients?"
                className="h-10 rounded-lg"
                data-testid="marketing-ai-question-input"
              />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-600 mb-1">Answer</div>
              <Textarea
                rows={3}
                value={draft.answer}
                onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))}
                placeholder="The answer Molly should give…"
                className="rounded-lg"
                data-testid="marketing-ai-answer-input"
              />
            </div>
            {isAdmin && (
              <div>
                <div className="text-xs font-medium text-zinc-600 mb-1">Trigger keywords (optional)</div>
                <Input
                  value={draft.keywords}
                  onChange={(e) => setDraft((d) => ({ ...d, keywords: e.target.value }))}
                  placeholder="vegan, animal, ingredients"
                  className="h-10 rounded-lg"
                  data-testid="marketing-ai-keywords-input"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-lg h-10 bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="marketing-ai-save-btn"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} className="mr-1" />}
                Save
              </Button>
              <Button variant="outline" onClick={cancel} className="rounded-lg h-10" data-testid="marketing-ai-cancel-btn">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!editing && (
        <Button
          onClick={startNew}
          className="rounded-xl h-11 w-full bg-violet-600 hover:bg-violet-700 text-white mb-4"
          data-testid="marketing-ai-add-btn"
        >
          <Plus size={16} className="mr-1.5" /> Add a Q&A
        </Button>
      )}

      {loading ? (
        <div className="py-10 flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 p-8 text-center" data-testid="marketing-ai-empty">
          <MessageSquare size={22} className="mx-auto text-zinc-400 mb-2" />
          <p className="text-sm text-zinc-500">
            No Q&A yet. Add your first one so Molly can answer customers for you.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="marketing-ai-list">
          {items.map((it) => (
            <li
              key={it.id}
              className="rounded-xl border border-zinc-200 p-3 flex items-start justify-between gap-3"
              data-testid="marketing-ai-item"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900">{it.question}</div>
                <div className="text-sm text-zinc-600 mt-0.5 line-clamp-3">{it.answer}</div>
                {it.status && it.status.toLowerCase() !== "approved" && (
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wide font-semibold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                    {it.status}
                  </span>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => startEdit(it)}
                  className="h-8 w-8 rounded-lg text-zinc-500 hover:text-violet-600 hover:bg-violet-50"
                  data-testid="marketing-ai-edit-btn"
                >
                  <Pencil size={15} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(it)}
                  className="h-8 w-8 rounded-lg text-zinc-500 hover:text-rose-600 hover:bg-rose-50"
                  data-testid="marketing-ai-delete-btn"
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
